<div class="len-step">
  <div class="step-header-row">
    <h3 class="step-title">Legal Entities</h3>
    <div class="header-actions">
      <!-- Refresh button -->
      @if (!isNewCustomer()) {
        <button class="refresh-btn" (click)="refreshMatches()" [disabled]="isRefreshing()">
          <i class="material-symbols-rounded" [class.spinning]="isRefreshing()">refresh</i>
          {{ isRefreshing() ? 'Refreshing...' : 'Refresh Matches' }}
        </button>
      }
      <!-- Undo merge button -->
      @if (canUndoMerge()) {
        <button class="undo-btn" (click)="undoMerge()">
          <i class="material-symbols-rounded">undo</i>
          Undo Merge
        </button>
      }
      <!-- Undo delete button -->
      @if (canUndoDelete()) {
        <button class="undo-btn" (click)="undoDelete()">
          <i class="material-symbols-rounded">undo</i>
          Undo Delete
        </button>
      }
    </div>
  </div>

  <app-parent-context-banner></app-parent-context-banner>
  <app-parent-conflict-resolver></app-parent-conflict-resolver>

  <!-- LEN List -->
  <div class="len-list">
    @for (len of legalEntities(); track len.groupId) {
      <div class="len-card"
           [class.merge-selected]="mergeSelectedMap[len.groupId]"
           [class.needs-attention]="fastTrackBlockingSet().has(len.groupId)">
        <!-- LEN Header Row -->
        <div class="len-header">
          <div class="len-title-area">
            <!-- Merge checkbox (only when multiple LENs exist) -->
            @if (legalEntities().length > 1) {
              <mat-checkbox
                class="merge-checkbox"
                title="Select for merge"
                [checked]="mergeSelectedMap[len.groupId]"
                (change)="toggleMergeSelection(len.groupId)"
              ></mat-checkbox>
            }

            <!-- Entity icon -->
            <div class="entity-icon len-icon">
              <i class="material-symbols-rounded">account_balance</i>
            </div>

            <!-- Editable name / Match dropdown -->
            <div class="len-name-field">
              <span class="entity-type-label len-type-label">Legal Entity</span>
              @if (lenSelectionMap[len.groupId] !== null) {
                <div class="match-dropdown-wrapper">
                  <app-score-select
                    [options]="lenScoreOptionsMap[len.groupId]"
                    [value]="lenSelectionMap[len.groupId]"
                    (valueChange)="onSelectionChange(len.groupId, $event)">
                  </app-score-select>
                </div>
              } @else {
                <div class="editable-field-wrapper" [class.field-error]="fieldErrors[len.groupId]?.['name']">
                  <input type="text" class="name-input" [value]="displayNameMap[len.groupId]" (input)="onNameChange(len.groupId, $any($event.target).value)" placeholder="Legal entity name" />
                  <i class="material-symbols-rounded edit-icon">edit</i>
                </div>
              }
              <!-- Billing Address -->
              <div class="billing-address-container" [class.has-errors]="lenBillingAddressIncomplete[len.groupId]" (click)="toggleEditBilling(len.groupId, $event)">
                <div class="billing-label">
                  Billing Address
                  <i class="material-symbols-rounded edit-icon">edit</i>
                </div>
                <div class="len-billing-address">
                  <div class="address-line-1">
                    <span [class.field-missing]="fieldErrors[len.groupId]?.['billingAddress.street1']"
                      (click)="onMissingBillingFieldClick(len.groupId, 'street1', $event)"
                      >{{ lenAddressDataMap[len.groupId]?.street1 || 'Street address' }}</span>@if (lenAddressDataMap[len.groupId]?.street2) {<span>, {{ lenAddressDataMap[len.groupId]?.street2 }}</span>}
                  </div>
                  <div class="address-line-2">
                    <span [class.field-missing]="fieldErrors[len.groupId]?.['billingAddress.city']"
                      (click)="onMissingBillingFieldClick(len.groupId, 'city', $event)"
                      >{{ lenAddressDataMap[len.groupId]?.city || 'City' }}</span>,
                    <span [class.field-missing]="fieldErrors[len.groupId]?.['billingAddress.state']"
                      (click)="onMissingBillingFieldClick(len.groupId, 'state', $event)"
                      >{{ lenAddressDataMap[len.groupId]?.state || 'State' }}</span>&nbsp;<span [class.field-missing]="fieldErrors[len.groupId]?.['billingAddress.zip']"
                      (click)="onMissingBillingFieldClick(len.groupId, 'zip', $event)"
                      >{{ lenAddressDataMap[len.groupId]?.zip || 'ZIP' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Match selector -->
          <app-entity-match-selector
            [groupId]="len.groupId"
            [match]="len.match"
            [alternatives]="len.alternatives"
            [extractedName]="displayNameMap[len.groupId]"
            [forcedNew]="isNewCustomer()"
            [forcedNewReason]="'Customer is new'"
            [exactLocked]="lenExactLockedMap[len.groupId] || false"
            [exactLockedReason]="'Legal entity already exists — linked to the existing record'"
            [showDropdown]="false"
            (selectionChange)="onSelectionChange(len.groupId, $event)">
          </app-entity-match-selector>
        </div>

        <!-- Full-width Billing Edit Form (Sliding down) -->
        @if (editingBillingMap[len.groupId]) {
          <div class="billing-edit-form-full">
            <div class="edit-form-header">
              <i class="material-symbols-rounded">location_city</i>
              <span>Edit Billing Address</span>
            </div>
            <div class="edit-fields-grid">
              <mat-form-field appearance="outline"
                [class.field-error]="fieldErrors[len.groupId]?.['billingAddress.street1']">
                <mat-label>Street Address</mat-label>
                <input matInput [id]="'len-' + len.groupId + '-street1'"
                  [value]="lenAddressDataMap[len.groupId].street1"
                  (input)="onBillingFieldChange(len.groupId, 'street1', $any($event.target).value)" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Address 2</mat-label>
                <input matInput [value]="lenAddressDataMap[len.groupId].street2" (input)="onBillingFieldChange(len.groupId, 'street2', $any($event.target).value)" />
              </mat-form-field>
              <div class="address-row">
                <mat-form-field appearance="outline" class="city-field"
                  [class.field-error]="fieldErrors[len.groupId]?.['billingAddress.city']">
                  <mat-label>City</mat-label>
                  <input matInput [id]="'len-' + len.groupId + '-city'"
                    [value]="lenAddressDataMap[len.groupId].city"
                    (input)="onBillingFieldChange(len.groupId, 'city', $any($event.target).value)" />
                </mat-form-field>
                <mat-form-field appearance="outline" class="state-field"
                  [class.field-error]="fieldErrors[len.groupId]?.['billingAddress.state']">
                  <mat-label>State</mat-label>
                  <mat-select [id]="'len-' + len.groupId + '-state'"
                    [value]="lenAddressDataMap[len.groupId].state"
                    (selectionChange)="onBillingFieldChange(len.groupId, 'state', $event.value)">
                    @for (s of stateOptions; track s) {
                      <mat-option [value]="s">{{ s }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" class="zip-field"
                  [class.field-error]="fieldErrors[len.groupId]?.['billingAddress.zip']">
                  <mat-label>ZIP</mat-label>
                  <input matInput [id]="'len-' + len.groupId + '-zip'"
                    [value]="lenAddressDataMap[len.groupId].zip"
                    (input)="onBillingFieldChange(len.groupId, 'zip', $any($event.target).value)"
                    maxlength="10" />
                </mat-form-field>
              </div>
            </div>
            <div class="edit-form-actions">
              <button class="btn-primary" (click)="toggleEditBilling(len.groupId, $event)">Save Address</button>
            </div>
          </div>
        }

        <!-- LEN duplicate-on-import warning -->
        @if (lenDuplicateKindMap[len.groupId]) {
          <div class="len-duplicate-warning"
               [class.len-duplicate-warning--info]="lenDuplicateKindMap[len.groupId] === 'safe-info'"
               [class.len-duplicate-warning--error]="lenDuplicateKindMap[len.groupId] === 'destructive-error'">
            <i class="material-symbols-rounded">{{ lenDuplicateKindMap[len.groupId] === 'safe-info' ? 'merge' : 'error' }}</i>
            <span>{{ lenDuplicateMessageMap[len.groupId] }}</span>
          </div>
        }

        <!-- Children (always visible) -->
        <div class="len-children">
          <div class="sa-list">
            @for (sa of len.serviceAddresses; track sa.groupId) {
              <div class="sa-row-simplified">
                <div class="sa-row-main">
                  <div class="sa-icon-col">
                    <i class="material-symbols-rounded">home_work</i>
                  </div>
                  <div class="sa-info-col">
                    <span class="entity-type-label sa-type-label">Address</span>
                    <div class="sa-address">{{ displayNameMap[sa.groupId] }}</div>
                    <div class="sa-utility">{{ saUtilitiesMap[sa.groupId] }}</div>
                  </div>
                  <div class="sa-stats-col">
                    <span class="stat-badge meter-stat">
                      <i class="material-symbols-rounded">speed</i>
                      {{ saCountsMap[sa.groupId]?.meterCount }} Meter{{ saCountsMap[sa.groupId]?.meterCount !== 1 ? 's' : '' }}
                    </span>
                    <button class="stat-badge bill-stat" (click)="previewBill(sa)" title="View Bills">
                      <i class="material-symbols-rounded">receipt_long</i>
                      {{ saCountsMap[sa.groupId]?.billCount }} Bill{{ saCountsMap[sa.groupId]?.billCount !== 1 ? 's' : '' }}
                      <i class="material-symbols-rounded view-arrow">
                        {{ (saBillsMap[sa.groupId]?.length > 1) && expandedBillSas[sa.groupId] ? 'expand_less' : 'arrow_forward' }}
                      </i>
                    </button>
                  </div>
                  <div class="sa-confidence-col">
                    @if (sa.match) {
                      <div class="confidence-badge" [class.high]="saConfidenceMap[sa.groupId] >= 80" [class.med]="saConfidenceMap[sa.groupId] >= 50 && saConfidenceMap[sa.groupId] < 80" [class.low]="saConfidenceMap[sa.groupId] < 50">
                        {{ saConfidenceMap[sa.groupId] }}% Match
                      </div>
                    } @else {
                      <span class="status-badge new-badge">
                        <i class="material-symbols-rounded">check</i>
                        New
                      </span>
                    }
                  </div>
                </div>
                @if (expandedBillSas[sa.groupId] && saBillsMap[sa.groupId]?.length > 1) {
                  <div class="expanded-bills">
                    @for (bill of saBillsMap[sa.groupId]; track bill.groupId) {
                      <div class="bill-row clickable"
                           [class.previewing]="previewingBillMap[bill.groupId]"
                           (click)="previewSpecificBill(bill)">
                        <i class="material-symbols-rounded bill-icon">receipt_long</i>
                        <span class="bill-dates">{{ billLabelMap[bill.groupId] }}</span>
                        <i class="material-symbols-rounded view-icon">visibility</i>
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>
        </div>
      </div>
    }
  </div>

  <!-- Floating action bar (merge button — matches dashboard hierarchy UX) -->
  <app-selection-action-bar
    [selectedEntityCount]="mergeSelection().length"
    [isHierarchyView]="true"
    (mergeEntities)="openMergeModal()"
    (deleteEntities)="deleteSelected()">
  </app-selection-action-bar>

  <!-- Merge modal (shared with dashboard) -->
  <app-merge-modal
    [isOpen]="mergeModalState.isOpen"
    [type]="'entity'"
    [candidates]="mergeModalState.candidates"
    [labelMap]="mergeModalState.labelMap"
    [subLabelMap]="mergeModalState.subLabelMap"
    subtitleNote="The selected entity's billing address will be kept."
    (closeModal)="closeMergeModal()"
    (confirm)="onMergeConfirmed($event)">
  </app-merge-modal>

  <!-- Last-item delete confirmation -->
  @if (showLastItemConfirm) {
    <div class="last-item-confirm-overlay" (click)="cancelLastItemDelete()">
      <div class="last-item-confirm-dialog" (click)="$event.stopPropagation()">
        <div class="confirm-icon">
          <i class="material-symbols-rounded">warning</i>
        </div>
        <h4>Remove all legal entities?</h4>
        <p>
          You're about to remove the last legal entity from this import.
          This will end the current session and return you to the upload screen.
        </p>
        <div class="confirm-actions">
          <button class="btn-secondary" (click)="cancelLastItemDelete()">Cancel</button>
          <button class="btn-danger" (click)="confirmLastItemDelete()">
            <i class="material-symbols-rounded">delete</i>
            Remove &amp; Exit
          </button>
        </div>
      </div>
    </div>
  }

  <!-- Validation Banner -->
  @if (validationSummary.totalErrors > 0) {
    <div class="validation-banner">
      <div class="validation-banner-title">
        <i class="material-symbols-rounded">block</i>
        Fix these before continuing
      </div>
      @for (group of validationSummary.errorGroups; track group.entityLabel) {
        <div class="validation-banner-group">
          <div class="group-label">{{ group.entityLabel }}</div>
          <ul>
            @for (field of group.fields; track field) {
              <li>{{ field }}</li>
            }
          </ul>
        </div>
      }
    </div>
  }

  <!-- Actions -->
  <div class="actions-bar">
    <button class="btn-secondary" (click)="goBack()">
      <i class="material-symbols-rounded">arrow_back</i>
      Back
    </button>
    <button class="btn-primary"
            [disabled]="hasBlockingLenDuplicate()"
            (click)="goForward()">
      Continue
      <i class="material-symbols-rounded">arrow_forward</i>
    </button>
  </div>
</div>
